import React, { useMemo } from 'react';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionToolbar from '../common/ActionToolbar';
import { USER_ROLES } from '../../utils/constants';

const DeliveryOrdersActions = ({
    showSearch = true,
    searchValue = '',
    onSearchChange,
    onRefresh,
    refreshLoading = false,
    statusValue = '',
    onStatusChange,
    userRole,
    supervisorValue,
    onSupervisorChange,
    supervisorOptions,
}) => {
    const { t } = useTranslation();
    const actions = useMemo(() => ([
        {
            key: 'refresh-orders',
            label: t('deliveryOrdersModule.actions.refresh'),
            icon: faRefresh,
            onClick: onRefresh,
            disabled: refreshLoading,
            variant: 'ghost',
            showLabelBreakpoint: 'sm',
            spinner: refreshLoading,
        },
    ]), [onRefresh, refreshLoading, t]);

    const statusOptions = useMemo(() => {
        const options = [
            { value: '', label: t('deliveryOrdersModule.actions.filters.all') },
            { value: 'ready_for_challan', label: t('deliveryOrdersModule.actions.status.ready_for_challan') },
            { value: 'supervisor_assigned', label: t('deliveryOrdersModule.actions.status.supervisor_assigned') },
            { value: 'loading', label: t('deliveryOrdersModule.actions.status.loading') },
            { value: 'loaded', label: t('deliveryOrdersModule.actions.status.loaded') },
            { value: 'in_transit', label: t('deliveryOrdersModule.actions.status.in_transit') },
        ];

        if (userRole === USER_ROLES.DELIVERY_SUPERVISOR) {
            return options.filter(opt => opt.value === '' || opt.value === 'supervisor_assigned' || opt.value === 'loading');
        }

        return options;
    }, [userRole, t]);

    const filters = useMemo(() => {
        // Account users always see only loaded orders — no filters at all.
        if (userRole === USER_ROLES.ACCOUNT_USER) return [];

        const filterList = [];

        // Operations managers were deliberately excluded from the STATUS filter in
        // 8403135 ("Add operations role") — their status set is fixed. Gate each filter
        // on its own role rule rather than one shared early return, so granting this
        // role the Supervisor filter does not hand back the Status filter with it.
        if (userRole !== USER_ROLES.OPERATIONS_MANAGER) {
            filterList.push({
                key: 'status-filter',
                type: 'select',
                label: t('deliveryOrdersModule.actions.filters.status'),
                value: statusValue,
                options: statusOptions,
                onChange: onStatusChange,
            });
        }

        // A supervisor's list is already scoped to their own orders, so the filter would
        // do nothing for them except disclose the full roster.
        if (userRole !== USER_ROLES.DELIVERY_SUPERVISOR) {
            filterList.push({
                key: 'supervisor-filter',
                type: 'select',
                label: t('deliveryOrdersModule.actions.filters.supervisor'),
                value: supervisorValue,
                options: supervisorOptions,
                onChange: onSupervisorChange,
            });
        }

        return filterList;
    }, [statusValue, onStatusChange, statusOptions, userRole, supervisorValue, onSupervisorChange, supervisorOptions, t]);

    return (
        <ActionToolbar
            showSearch={showSearch}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={t('deliveryOrdersModule.actions.searchPlaceholder')}
            filters={filters}
            actions={actions}
        />
    );
};

export default DeliveryOrdersActions;
